// @ts-nocheck
import { Component } from "@theme/component";
import { debounce, watchAny } from "@theme/utilities";

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 0;

/**
 * Sequentially fade the product cards inside a freshly-activated content panel.
 * Layered on top of the panel crossfade; animates transform/opacity only so it
 * never affects the measured panel height, and auto-cleans up once finished
 * (no fill, so cards revert to their natural state).
 * @param {HTMLElement} panel
 */
function staggerCardsIn(panel) {
  if (!panel || typeof panel.animate !== "function") return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const cards = panel.querySelectorAll(".grid > *");
  cards.forEach((card, i) => {
    card.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 300,
      delay: i * 60,
      easing: "ease",
      fill: "backwards",
    });
  });
}

/**
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} previousWrapper - The previously active menu item.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} menuContainer - The menu container.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 * @property {HTMLElement[]} [menuItem] - The menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ["menuContainer"];

  #abortController;

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    previousWrapper: null,
  };

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.outerMenuWrapper = this.querySelector('[ref="menuWrapper"]');

    // Keep the dropdown open while the pointer is anywhere in the full-width
    // header row — including the empty areas beside the centered menu — instead
    // of only over the narrow menu container. The dropdown is a DOM descendant
    // of the row, so moving into it never counts as leaving the region.
    this.hoverRegion = this.closest(".header__row") || this.refs.menuContainer;

    this.hoverRegion.addEventListener(
      "pointerleave",
      () => this.#debouncedDeactivate(),
      {
        signal: this.#abortController.signal,
      }
    );

    this.hoverRegion.addEventListener(
      "pointerenter",
      () => {
        this.#debouncedDeactivate.cancel();
      },
      {
        signal: this.#abortController.signal,
      }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #deactivate = () => {
    if (this.outerMenuWrapper) {
      this.outerMenuWrapper.classList.remove("active");
    }
    if (!this.#state.activeItem) return;
    const menuIndex = this.#state.activeItem.dataset.index;
    const menuWrapper = this.querySelector(
      `[data-menu-wrapper-index="${menuIndex}"]`
    );
    if (menuWrapper) {
      menuWrapper.classList.remove("active");
      menuWrapper.classList.add("hidden");
    }
    this.#state.activeItem.classList.remove("active");

    // Use requestAnimationFrame for smooth animation
    requestAnimationFrame(() => {
      this.refs.menuContainer.style.setProperty("--menu-wrapper-height", "0px");
    });

    this.#state.activeItem = null;
    this.#state.previousWrapper = null;
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    if (this.#state.activeItem) {
      this.#state.activeItem.classList.remove("active");
    }

    this.#state.activeItem =
      event.target instanceof HTMLElement ? event.target : null;

    if (!this.#state.activeItem) return;

    if (this.outerMenuWrapper) {
      this.outerMenuWrapper.classList.add("active");
    }

    const menuIndex = this.#state.activeItem.dataset.index;
    const menuWrapper = this.querySelector(
      `[data-menu-wrapper-index="${menuIndex}"]`
    );

    if (menuIndex == this.#state.previousWrapper?.dataset.menuWrapperIndex)
      return;

    this.#state.activeItem.classList.add("active");

    if (menuWrapper) {
      // Remove hidden class and prepare for display
      menuWrapper.classList.remove("hidden");

      // Get the height first (while still potentially hidden but in DOM)
      const targetHeight = menuWrapper.clientHeight || 0;

      // Use requestAnimationFrame for smooth height transition
      requestAnimationFrame(() => {
        // Set height first
        if (targetHeight > 0) {
          this.refs.menuContainer.style.setProperty(
            "--menu-wrapper-height",
            `${targetHeight}px`
          );
        } else {
          this.refs.menuContainer.style.setProperty(
            "--menu-wrapper-height",
            "0px"
          );
        }

        // Add active class immediately after height is set for opacity/transform animation
        requestAnimationFrame(() => {
          menuWrapper.classList.add("active");
        });
      });
    } else {
      this.refs.menuContainer.style.setProperty("--menu-wrapper-height", "0px");
    }

    if (this.#state.previousWrapper) {
      this.#state.previousWrapper.classList.remove("active");
      this.#state.previousWrapper.classList.add("hidden");
    }

    this.#state.previousWrapper =
      menuWrapper instanceof HTMLElement ? menuWrapper : null;
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };
}

if (!customElements.get("header-menu")) {
  customElements.define("header-menu", HeaderMenu);
}

class LayoutMenuControl extends Component {
  #activeIndex;
  #menuContainer;
  constructor() {
    super();

    this.#activeIndex = watchAny(0, (newVal, oldVal) => {
      if (oldVal === newVal) return;

      const contents = this.refs.ly2_contents || [];
      const titles = this.refs.ly2_titles || [];

      if (contents[oldVal]) contents[oldVal].classList.add("hidden");
      if (contents[newVal]) contents[newVal].classList.remove("hidden");

      if (titles[oldVal]) titles[oldVal].classList.remove("bg-gray-100");
      if (titles[newVal]) titles[newVal].classList.add("bg-gray-100");

      requestAnimationFrame(() => {
        if (!this.#menuContainer || !this.refs.ly2_container) return;
        const containerHeight = this.refs.ly2_container.clientHeight;
        this.#menuContainer.style.setProperty(
          "--menu-wrapper-height",
          `${containerHeight}px`
        );
      });
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#menuContainer = this.closest('[ref="menuContainer"]');
  }

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  mouseIn = (event) => {
    const el = event.target instanceof HTMLElement ? event.target : null;
    if (!el) return;
    const menuIndex = parseInt(el.dataset.menuIndex || "0", 10);
    this.#activeIndex.value = menuIndex;
  };
}
if (!customElements.get("layout-menu-control")) {
  customElements.define("layout-menu-control", LayoutMenuControl);
}

class DropdownMenuControl extends Component {
  #activeLeaf;
  #menuContainer;

  constructor() {
    super();

    this.#activeLeaf = watchAny(0, (newVal, oldVal) => {
      if (oldVal === newVal) return;

      const contents = this.refs.ly4_contents || [];
      const items = this.refs.ly4_items || [];

      if (contents[oldVal]) contents[oldVal].classList.remove("ly4-fade-in");
      if (contents[newVal]) {
        contents[newVal].classList.add("ly4-fade-in");
        staggerCardsIn(contents[newVal]);
      }

      if (items[oldVal]) items[oldVal].classList.remove("bg-gray-100");
      if (items[newVal]) items[newVal].classList.add("bg-gray-100");

      requestAnimationFrame(() => {
        const containerHeight = this.refs.ly4_container.clientHeight;
        if (this.#menuContainer) {
          this.#menuContainer.style.setProperty(
            "--menu-wrapper-height",
            `${containerHeight}px`
          );
        }
      });
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#menuContainer = this.closest('[ref="menuContainer"]');
  }

  #syncHeight = () => {
    requestAnimationFrame(() => {
      if (!this.#menuContainer) return;
      const containerHeight = this.refs.ly4_container.clientHeight;
      this.#menuContainer.style.setProperty(
        "--menu-wrapper-height",
        `${containerHeight}px`
      );
    });
  };

  /**
   * Toggle category panel open/closed.
   * @param {PointerEvent | MouseEvent} event
   */
  toggleCategory = (event) => {
    const btn =
      event.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : event.target instanceof HTMLElement
          ? event.target.closest("[data-category-index]")
          : null;
    if (!btn) return;

    const index = parseInt(btn.dataset.categoryIndex || "0", 10);
    const panels = this.refs.ly4_categoryPanels || [];
    const titles = this.refs.ly4_categoryTitles || [];
    const panel = panels[index];
    const title = titles[index];
    if (!panel || !title) return;

    const isOpen = title.classList.contains("ly4-category-open");
    title.classList.toggle("ly4-category-open", !isOpen);
    panel.classList.toggle("ly4-panel-open", !isOpen);

    // Sync mega-menu wrapper height progressively during animation
    this.#syncHeight();
    setTimeout(this.#syncHeight, 160);
    setTimeout(this.#syncHeight, 320);
  };

  /**
   * Activate the hovered leaf item — show its right-side content panel.
   * @param {PointerEvent | FocusEvent} event
   */
  leafIn = (event) => {
    const el = event.target instanceof HTMLElement ? event.target : null;
    if (!el) return;
    const leaf = el.closest("[data-leaf-index]");
    if (!(leaf instanceof HTMLElement)) return;
    const leafIndex = parseInt(leaf.dataset.leafIndex || "0", 10);
    this.#activeLeaf.value = leafIndex;
  };
}
if (!customElements.get("dropdown-menu-control")) {
  customElements.define("dropdown-menu-control", DropdownMenuControl);
}

const FOCUS_HOVER_DELAY = 80;

class FocusMenuControl extends Component {
  #activeSecondary;
  #activeLeaf;
  #menuContainer;

  constructor() {
    super();

    this.#activeSecondary = watchAny(0, (newVal, oldVal) => {
      if (oldVal === newVal) return;
      const secondaries = this.refs.ly5_secondaries || [];
      const panels = this.refs.ly5_tertiaryPanels || [];

      if (secondaries[oldVal]) {
        secondaries[oldVal].classList.remove("ly5-secondary-active");
      }
      if (secondaries[newVal]) {
        secondaries[newVal].classList.add("ly5-secondary-active");
      }
      if (panels[oldVal]) panels[oldVal].classList.remove("ly5-fade-in");
      if (panels[newVal]) panels[newVal].classList.add("ly5-fade-in");

      this.#syncHeight();
    });

    this.#activeLeaf = watchAny(0, (newVal, oldVal) => {
      if (oldVal === newVal) return;
      const tertiaries = this.refs.ly5_tertiaries || [];
      const contents = this.refs.ly5_contents || [];

      if (tertiaries[oldVal]) {
        tertiaries[oldVal].classList.remove("ly5-tertiary-active");
      }
      if (tertiaries[newVal]) {
        tertiaries[newVal].classList.add("ly5-tertiary-active");
      }
      if (contents[oldVal]) contents[oldVal].classList.remove("ly5-fade-in");
      if (contents[newVal]) {
        contents[newVal].classList.add("ly5-fade-in");
        staggerCardsIn(contents[newVal]);
      }

      this.#syncHeight();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#menuContainer = this.closest('[ref="menuContainer"]');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#debouncedSecondaryIn.cancel?.();
    this.#debouncedTertiaryIn.cancel?.();
  }

  #syncHeight = () => {
    requestAnimationFrame(() => {
      if (!this.#menuContainer || !this.refs.ly5_container) return;
      const containerHeight = this.refs.ly5_container.clientHeight;
      this.#menuContainer.style.setProperty(
        "--menu-wrapper-height",
        `${containerHeight}px`
      );
    });
  };

  #applySecondary = (node) => {
    const secondaryIndex = parseInt(node.dataset.secondaryIndex || "0", 10);
    const firstLeaf = parseInt(node.dataset.firstLeafIndex || "0", 10);
    this.#activeSecondary.value = secondaryIndex;
    this.#activeLeaf.value = firstLeaf;
  };

  #applyTertiary = (node) => {
    const leafIndex = parseInt(node.dataset.leafIndex || "0", 10);
    this.#activeLeaf.value = leafIndex;
  };

  #debouncedSecondaryIn = debounce((node) => this.#applySecondary(node), FOCUS_HOVER_DELAY);
  #debouncedTertiaryIn = debounce((node) => this.#applyTertiary(node), FOCUS_HOVER_DELAY);

  /**
   * Hover on a secondary item — switch tertiary panel and jump to its first tertiary.
   * @param {PointerEvent | FocusEvent} event
   */
  secondaryIn = (event) => {
    const el = event.target instanceof HTMLElement ? event.target : null;
    if (!el) return;
    const node = el.closest("[data-secondary-index]");
    if (!(node instanceof HTMLElement)) return;
    // Cancel any pending tertiary switch — secondary takes precedence
    this.#debouncedTertiaryIn.cancel?.();
    this.#debouncedSecondaryIn(node);
  };

  /**
   * Hover on a tertiary item — switch the right-side product panel.
   * @param {PointerEvent | FocusEvent} event
   */
  tertiaryIn = (event) => {
    const el = event.target instanceof HTMLElement ? event.target : null;
    if (!el) return;
    const node = el.closest("[data-leaf-index]");
    if (!(node instanceof HTMLElement)) return;
    this.#debouncedTertiaryIn(node);
  };
}
if (!customElements.get("focus-menu-control")) {
  customElements.define("focus-menu-control", FocusMenuControl);
}
